import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Store } from 'lucide-react';

/**
 * Buyer-facing collection browse modal.
 *
 * Deliberately mirrors the seller Dashboard → Catalogue Broadcast
 * `CollectionDetailModal` (Broadcast.jsx) browsing experience — a stable
 * fixed overlay with a scrollable product grid and an ✕ / backdrop close —
 * adapted to the PUBLIC (no-auth) buyer endpoint `/api/collections/{cid}/view`.
 *
 * Why a modal (not chat injection): the products render once from a single
 * fetch and stay mounted for the whole lifetime of the modal. There is NO
 * timer, NO SSE reload and NO chat-message state driving them, so images
 * never disappear while the buyer browses. Opening it does NOT hit the AI
 * chat endpoint, so browsing never creates a message, cart item or quotation.
 */
export const BuyerCollectionModal = ({ cid, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  // "Order this" → reuse the EXISTING buyer-chat flow: open this collection's
  // buyer chat with ONLY the selected product as context (via ?product=). The
  // chat shows just that one product card — never the whole collection. No new
  // endpoint / order mechanism is introduced.
  const orderProduct = (code) => {
    if (!code) return;
    if (onClose) onClose();
    navigate(`/c/${cid}?product=${encodeURIComponent(code)}`);
  };

  useEffect(() => {
    if (!cid) return;
    let alive = true;
    setLoading(true);
    setData(null);
    const base = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${base}/api/collections/${cid}/view`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cid]);

  if (!cid) return null;

  const imgOf = (p) =>
    p.processed_image_url || (Array.isArray(p.images) && p.images[0]) || p.original_image_url || null;
  const products = (data?.products || []).filter((p) => imgOf(p));

  const downloadAllImages = async () => {
  if (downloading || products.length === 0) return;
  setDownloading(true);
  try {
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      const src = imgOf(p);
      if (typeof src !== 'string' || !src) continue;

      const safeCode = String(p.product_code || '')
        .trim()
        .replace(/[^A-Za-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '');
      const filename = `${safeCode || `image-${i + 1}`}.jpg`;

      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (err) {
        console.error('Failed to download image:', src, err);
      }

      if (i < products.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    }
  } finally {
    setDownloading(false);
  }
};
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="buyer-collection-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[calc(100vh_-_2rem)] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-black/10 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-neutral-900 text-[15px] sm:text-[16px] truncate" data-testid="buyer-collection-name">
              {data?.name || 'Collection'}
            </div>
            {data && (
              <div className="text-[12px] text-neutral-500 truncate">
                {data.product_count} {data.product_count === 1 ? 'product' : 'products'}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-black/5 rounded-lg shrink-0 text-neutral-700"
            aria-label="Close"
            data-testid="buyer-collection-close"
          >
            <span aria-hidden="true" className="text-[18px] leading-none">✕</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4" data-testid="buyer-collection-scroll">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-neutral-500">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : !data ? (
            <div className="py-16 text-center text-[13px] text-neutral-500" data-testid="buyer-collection-error">
              Could not load this collection.
            </div>
          ) : (
            <>
              {data.cover_image_url && (
                <div className="rounded-xl overflow-hidden border border-black/10">
                  <img src={data.cover_image_url} alt="Cover" className="w-full h-auto object-cover" data-testid="buyer-collection-cover" />
                </div>
              )}
              {data.description && (
                <div className="text-[13px] text-neutral-800 whitespace-pre-wrap leading-relaxed" data-testid="buyer-collection-description">
                  {data.description}
                </div>
              )}
              {products.length === 0 ? (
                <div className="py-10 text-center text-[13px] text-neutral-500" data-testid="buyer-collection-empty">
                  <Store className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                  No products to show in this collection yet.
                </div>
                            ) : (
                <>
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={downloadAllImages}
                      disabled={downloading || products.length === 0}
                      className="text-[12px] font-semibold text-fab-primary px-3 py-1.5 rounded-full border border-fab-primary/50 bg-fab-primary/15 hover:bg-fab-primary/25 disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="buyer-collection-download-all"
                    >
                      {downloading ? 'Downloading…' : `Download All Images (${products.length})`}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="buyer-collection-products">
                  {products.map((p) => (
                    <div key={p.id} className="rounded-lg overflow-hidden border border-black/10 flex flex-col" data-testid={`buyer-collection-product-${p.product_code}`}>
                      <div className="aspect-square bg-neutral-100">
                        <img src={imgOf(p)} alt={p.product_code || 'Product'} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="px-1.5 pt-1 text-[10px] font-mono text-neutral-700 truncate text-center">
                        {p.product_code}
                      </div>
                      {(p.price != null || p.moq != null) && (
                        <div className="px-1.5 text-[10px] text-fab-primary font-semibold text-center truncate">
                          {p.price != null ? `₹${p.price}` : ''}{p.moq != null ? `${p.price != null ? ' · ' : ''}MOQ ${p.moq}` : ''}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => orderProduct(p.product_code)}
                        className="mt-1 mx-1.5 mb-1.5 text-[10px] font-medium text-fab-primary px-2 py-1 rounded-full border border-fab-primary/25 bg-fab-primary/5 hover:bg-fab-primary/10"
                        data-testid={`buyer-collection-order-${p.product_code}`}
                      >
                        Order this
                      </button>
                    </div>
                                ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyerCollectionModal;
